/**
 * useAlertSocket — /ws/notifications 실시간 알림 구독 훅 (모바일 realtime.js 패턴 이식).
 *
 * 백엔드 fan_out_alert 가 보내는 {type:'ALERT', ...} 만 콜백으로 전달한다.
 * 재연결: 최대 5회, 1s→5s 백오프. 토큰 변경(로그인/refresh) 시 재연결.
 * 연결은 앱당 1개(AppHeader)만 유지 — 페이지는 CustomEvent('pharm:alert')로 수신.
 */
import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/store/authStore'

export interface AlertMessage {
  type: string
  alert_type: string
  severity: string
  title: string
  body: string
  patient_id?: number
  ref_schedule_id?: number | null
  ref_report_id?: number | null
}

function wsUrl(token: string): string {
  // dev: vite proxy /ws → 37086 / prod(docker): VITE_WS_URL 주입
  const base =
    (import.meta.env.VITE_WS_URL as string | undefined) ||
    window.location.origin.replace(/^http/, 'ws')
  return `${base}/ws/notifications?token=${encodeURIComponent(token)}`
}

/** 실시간 알림 소켓 구독 훅 — 수신 시 onAlert 콜백을 호출한다. */
export function useAlertSocket(onAlert: (msg: AlertMessage) => void): void {
  const token = useAuthStore((s) => s.token)
  const cbRef = useRef(onAlert)
  cbRef.current = onAlert

  useEffect(() => {
    if (!token) return
    let cancelled = false
    let retries = 0
    let ws: WebSocket | null = null
    let timer: ReturnType<typeof setTimeout> | null = null

    const connect = () => {
      if (cancelled) return
      try {
        ws = new WebSocket(wsUrl(token))
      } catch {
        return
      }
      ws.onopen = () => {
        retries = 0
      }
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data)
          if (msg?.type === 'ALERT') cbRef.current(msg as AlertMessage)
        } catch {
          /* 비JSON(PONG 등) 무시 */
        }
      }
      ws.onclose = () => {
        if (cancelled || retries >= 5) return
        retries += 1
        timer = setTimeout(connect, Math.min(1000 * retries, 5000))
      }
      ws.onerror = () => {
        ws?.close()
      }
    }

    connect()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      ws?.close()
    }
  }, [token])
}

export default useAlertSocket
